import React from 'react';
import styled from 'styled-components';
import { Icon, MicroIcon } from 'styleguide/Icons';
import { Modal } from 'styleguide/Modal';
import {
  ExerciseLibraryViewModel,
  isKeyedExerciseTemplate,
} from '../ExerciseLibrary/ExerciseLibraryViewModel';
import { View } from 'styleguide/View';
import { P1, P2, P3 } from 'styleguide/Texts';
import { CornerRadius, Spacing } from 'styleguide/spacing';
import { PrimaryButton, TertiaryButton } from 'styleguide/Buttons';
import { Select, isUpdate } from 'components/Multiselect';
import { equipmentFilters, muscleGroupFilters } from 'hevy-shared';
import { equipmentFilterToHuman, muscleGroupFilterToHuman } from 'exercises/utils';
import { SearchInput } from 'components/SearchInput';
import { AddExerciseCell } from 'components/ExerciseLibrary/components/AddExerciseCell';
import { DividerLine } from 'styleguide/DividerLine';
import { Observer } from 'mobx-react-lite';
import { colors } from 'styleguide/colors';
import { FlexRow } from 'styleguide/Row';
import { t } from 'translations/t';
import { stores } from 'state/stores';

const Container = styled(View)`
  width: 500px;
  flex: 1;
`;

const DismissButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  cursor: pointer;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const TopContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${Spacing.md}px;
  gap: ${Spacing.sm}px;
`;

const ExercisesListContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  overflow-y: scroll;
`;

export interface ExerciseLibraryModalProps {
  vm: ExerciseLibraryViewModel;
  hidePlusIcons?: boolean;
  onSelectExercise?: (templateId: string) => void;
  onCreateExercise?: () => void;
  selectedExerciseId?: string;
  isOpen: boolean;
  onClose: () => void;
}

export const ExerciseLibraryModal = ({
  vm,
  hidePlusIcons,
  selectedExerciseId,
  onSelectExercise,
  onCreateExercise,
  isOpen,
  onClose,
}: ExerciseLibraryModalProps) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} contentStyle={{ height: '100%', padding: 0 }}>
      <Observer>
        {() => (
          <Container>
            <TopContainer>
              <FlexRow style={{ marginBottom: Spacing.sm, justifyContent: 'space-between' }}>
                <TitleContainer>
                  <P1>{t('web.exerciseLibraryModal.selectExercise')}</P1>
                </TitleContainer>
                {onCreateExercise && (
                  <TertiaryButton
                    onClick={() => {
                      if (onCreateExercise) onCreateExercise();
                      onClose();
                    }}
                    style={{ gap: Spacing.sm }}
                  >
                    <MicroIcon tint={colors.primary} type="plus" />
                    {t('web.exerciseLibrary.customExercise')}
                  </TertiaryButton>
                )}
                <DismissButtonContainer onClick={onClose}>
                  <Icon type="cross" tint={colors.grey} />
                </DismissButtonContainer>
              </FlexRow>
              <Select
                options={equipmentFilters.map(f => ({
                  value: f,
                  label: equipmentFilterToHuman(f),
                }))}
                value={equipmentFilters
                  .map(f => ({ value: f, label: equipmentFilterToHuman(f) }))
                  .find(opt => opt.value === vm.equipmentFilter)}
                onChange={e => {
                  if (isUpdate(e)) {
                    vm.onEquipmentFilterUpdate(e.value);
                  }
                }}
              />
              <Select
                options={muscleGroupFilters.map(f => ({
                  value: f,
                  label: muscleGroupFilterToHuman(f),
                }))}
                value={muscleGroupFilters
                  .map(f => ({ value: f, label: muscleGroupFilterToHuman(f) }))
                  .find(opt => opt.value === vm.musleGroupFilter)}
                onChange={e => {
                  if (isUpdate(e)) {
                    vm.onMuscleGroupFilterUpdate(e.value);
                  }
                }}
              />

              <SearchInput
                type="primary"
                value={vm.searchText}
                onChange={vm.onSearchTextUpdate}
                placeholder={t('web.exerciseLibrary.searchExercises')}
              />
            </TopContainer>
            <DividerLine></DividerLine>
            <ExercisesListContainer>
              <View
                style={{
                  flex: 1,
                  marginTop: Spacing.md,
                  marginBottom: Spacing.md,
                  outline: 'none',
                  backgroundColor: 'transparent',
                }}
              >
                {vm.data.map(e => {
                  if (isKeyedExerciseTemplate(e)) {
                    return (
                      <AddExerciseCell
                        key={e.key}
                        highlight={e.id === selectedExerciseId}
                        image={e.thumbnail_url}
                        title={stores.exerciseTemplates.getExerciseTitle(e.id) || ''}
                        muscleGroup={e.muscle_group}
                        isCustom={e.is_custom}
                        hidePlusIcons={hidePlusIcons}
                        onClick={() => {
                          if (onSelectExercise) onSelectExercise(e.id);
                          onClose();
                        }}
                        style={
                          hidePlusIcons
                            ? {
                                borderRadius: CornerRadius.md,
                                marginInlineEnd: Spacing.sm,
                                marginInlineStart: Spacing.sm,
                              }
                            : {}
                        }
                      />
                    );
                  }

                  switch (e.type) {
                    case 'section':
                      return (
                        <View
                          style={{
                            marginInlineEnd: Spacing.md,
                            marginInlineStart: Spacing.md,
                            marginBottom: Spacing.sm,
                          }}
                        >
                          <P2 type="secondary">{e.title}</P2>
                        </View>
                      );
                    case 'spacer':
                      return (
                        <DividerLine style={{ marginTop: Spacing.sm, marginBottom: Spacing.md }} />
                      );
                    case 'no-exercises-match-search':
                      return (
                        <View
                          style={{
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginBottom: Spacing.md,
                            padding: Spacing.md,
                          }}
                        >
                          <P2
                            style={{
                              textAlign: 'center',
                              marginBottom: Spacing.sm,
                              marginTop: Spacing.md,
                            }}
                          >
                            {t('web.exerciseLibrary.cantFind', { searchText: vm.searchText })}
                          </P2>
                          <P3 style={{ textAlign: 'center', marginBottom: Spacing.sm }}>
                            {t('web.exerciseLibrary.weDontHaveThatEx')}
                          </P3>
                          <PrimaryButton
                            style={{ marginTop: Spacing.md }}
                            title={t('web.exerciseLibrary.createNewExBtn')}
                            onClick={() => {
                              if (onCreateExercise) onCreateExercise();
                              onClose();
                            }}
                          />
                        </View>
                      );
                  }
                })}
              </View>
            </ExercisesListContainer>
          </Container>
        )}
      </Observer>
    </Modal>
  );
};
